import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Award, Users, Globe, TrendingUp } from 'lucide-react';

const AboutPage = () => {
  const values = [
    {
      icon: <Award size={48} />,
      title: 'Excellence',
      description: 'Over 50 years of combined experience in diamond cultivation and cutting',
    },
    {
      icon: <Users size={48} />,
      title: 'Customer First',
      description: 'Building lasting partnerships with retailers and jewellers',
    },
    {
      icon: <Globe size={48} />,
      title: 'Sustainability',
      description: 'Committed to a cleaner, more sustainable planet for future generations',
    },
    {
      icon: <TrendingUp size={48} />,
      title: 'Innovation',
      description: 'Advanced scientific knowledge and sophisticated technology',
    },
  ];

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      <Navbar />

      {/* Hero Section */}
      <section className="section-padding bg-[var(--bg-secondary)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="hero-large mb-6">About Magnificent Diamond</h1>
            <p className="body-large">
              Where luxury embraces science to create diamonds that are REAL, PURE, and BETTER
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="hero-medium mb-6">Our Story</h2>
              <p className="body-regular mb-4">
                Welcome to Magnificent Diamond, a leading and trusted manufacturer and supplier of lab-grown diamonds.
                Our Customer First Approach, Consistent Assortment in every parcel, Stable supply and Best value in
                total cost of ownership makes us the 'preferred partner' for Retailers and Jewellers in USA.
              </p>
              <p className="body-regular mb-4">
                At Magnificent Diamond, luxury embraces science to give you diamonds which are REAL – PURE – BETTER.
              </p>
              <p className="body-regular">
                A cleaner, more sustainable planet earth in the coming time for future generations is what we believe
                to be 'true luxury'.
              </p>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1599707367072-cd6ada2bc375"
                alt="Lab-grown diamonds"
                className="w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="section-padding-small bg-[var(--bg-secondary)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://images.unsplash.com/photo-1631013636761-c533d81e96a4"
                alt="Diamond precision"
                className="w-full h-[500px] object-cover"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="hero-medium mb-6">Advanced Technology</h2>
              <p className="body-regular mb-4">
                With advanced scientific knowledge and sophisticated technology, our technology partners with their
                team of scientists and researchers (with over 50 years of combined experience in growing the rough
                diamond in a lab) have found a way, rather perfected a way to conceive and create a rough diamond
                outside the womb of mother earth in a lab with the seed of a mined diamond.
              </p>
              <p className="body-regular mb-4">
                Master diamond cutters then cut and polish this rough diamond to give you the perfect diamond – A
                Magnificent Diamond.
              </p>
              <p className="body-regular">
                The process is similar to IVF - using natural seeds to create something beautiful and real through
                scientific excellence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Authenticity Section */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="hero-medium mb-6">100% Real Diamonds</h2>
            <p className="body-large mb-6">
              A Magnificent diamond created in a lab has the exact same chemical, physical and optical properties as
              an earth mined diamond.
            </p>
            <p className="body-regular">
              Trained jewellers, diamantaires or gemologists cannot tell the difference between a polished lab grown
              diamond and a polished earth mined diamond as they are exactly the same except their point of origin.
              Only specialised machines with advanced technology in diamond grading/certifying Labs can distinguish
              between a Lab grown diamond and an earth mined diamond.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-[var(--accent-green-pale)] p-8">
              <h3 className="heading-2 mb-4 text-[var(--accent-green-dark)]">Lab-Grown Diamonds</h3>
              <ul className="space-y-3">
                <li className="body-regular flex items-start gap-2">
                  <span className="text-[var(--accent-green-medium)] mt-1">•</span>
                  <span>Identical chemical composition</span>
                </li>
                <li className="body-regular flex items-start gap-2">
                  <span className="text-[var(--accent-green-medium)] mt-1">•</span>
                  <span>Same physical properties</span>
                </li>
                <li className="body-regular flex items-start gap-2">
                  <span className="text-[var(--accent-green-medium)] mt-1">•</span>
                  <span>Equal optical brilliance</span>
                </li>
                <li className="body-regular flex items-start gap-2">
                  <span className="text-[var(--accent-green-medium)] mt-1">•</span>
                  <span>Earth-friendly creation</span>
                </li>
                <li className="body-regular flex items-start gap-2">
                  <span className="text-[var(--accent-green-medium)] mt-1">•</span>
                  <span>Certified conflict-free</span>
                </li>
                <li className="body-regular flex items-start gap-2">
                  <span className="text-[var(--accent-green-medium)] mt-1">•</span>
                  <span>Exceptional value</span>
                </li>
              </ul>
            </div>

            <div className="bg-[var(--bg-secondary)] p-8">
              <h3 className="heading-2 mb-4">Mined Diamonds</h3>
              <ul className="space-y-3">
                <li className="body-regular flex items-start gap-2">
                  <span className="text-[var(--text-light)] mt-1">•</span>
                  <span>Identical chemical composition</span>
                </li>
                <li className="body-regular flex items-start gap-2">
                  <span className="text-[var(--text-light)] mt-1">•</span>
                  <span>Same physical properties</span>
                </li>
                <li className="body-regular flex items-start gap-2">
                  <span className="text-[var(--text-light)] mt-1">•</span>
                  <span>Equal optical brilliance</span>
                </li>
                <li className="body-regular flex items-start gap-2">
                  <span className="text-[var(--text-light)] mt-1">•</span>
                  <span>Land extraction/destruction</span>
                </li>
                <li className="body-regular flex items-start gap-2">
                  <span className="text-[var(--text-light)] mt-1">•</span>
                  <span>Potential conflict issues</span>
                </li>
                <li className="body-regular flex items-start gap-2">
                  <span className="text-[var(--text-light)] mt-1">•</span>
                  <span>Higher cost</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding-small bg-[var(--bg-secondary)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="hero-medium mb-4">Our Values</h2>
            <p className="body-large">What makes us your preferred partner</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center">
                <div className="text-[var(--accent-green-medium)] mb-4 flex justify-center">{value.icon}</div>
                <h3 className="heading-3 mb-3">{value.title}</h3>
                <p className="body-regular">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
